import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ordersAPI } from '../../services/api';
import api from '../../services/api';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const PaymentCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
  const [message, setMessage] = useState('Verifying your payment...');
  const [amount, setAmount] = useState<number | null>(null);
  const [newBalance, setNewBalance] = useState<number | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [paymentType, setPaymentType] = useState<'wallet' | 'order'>('wallet');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference');
      const gateway = searchParams.get('gateway') || 'paystack';
      
      if (!reference) {
        setStatus('failed');
        setMessage('Invalid payment reference');
        return;
      }

      try {
        // Try order payment verification first
        let orderVerificationFailed = false;
        let orderErrorMessage = '';
        
        try {
          let orderResponse;
          
          if (gateway === 'seerbit') {
            orderResponse = await ordersAPI.verifyPaymentSeerBit(reference);
          } else {
            orderResponse = await ordersAPI.verifyPayment(reference);
          }
          
          if (orderResponse.status === 'success' && orderResponse.verified) {
            // This is an order payment and it succeeded
            setPaymentType('order');
            setStatus('success');
            setMessage(`Order payment successful via ${gateway === 'seerbit' ? 'SeerBit' : 'Paystack'}! Your order has been created.`);
            const responseOrderId = orderResponse.order_id || null;
            setOrderId(responseOrderId);

            // Redirect to order tracking page after 3 seconds
            setTimeout(() => {
              if (responseOrderId) {
                navigate(`/orders/${responseOrderId}`, { replace: true });
              } else {
                navigate('/orders', { replace: true });
              }
            }, 3000);
            return;
          } else if (orderResponse.status === 'error' || orderResponse.verified === false) {
            // Order verification explicitly failed
            setStatus('failed');
            setMessage(orderResponse.message || 'Order payment verification failed');
            return;
          }
        } catch (orderError: any) {
          // If order verification fails (404 or other error), try wallet verification
          orderVerificationFailed = true;
          orderErrorMessage = orderError.response?.data?.detail || orderError.message || '';
          console.log('Not an order payment, trying wallet verification...', orderErrorMessage);
        }

        // If not an order payment, try wallet top-up verification
        setPaymentType('wallet');
        
        try {
          const walletResponse = await api.post('/api/wallet/topup/verify', { reference });

          if (walletResponse.data.status === 'success' && walletResponse.data.verified) {
            setStatus('success');
            setMessage(walletResponse.data.message || 'Payment successful!');
            setAmount(walletResponse.data.amount);
            setNewBalance(walletResponse.data.new_balance);

            // Redirect to customer dashboard after 3 seconds
            setTimeout(() => {
              navigate('/customer-dashboard', { replace: true });
            }, 3000);
          } else {
            setStatus('failed');
            setMessage(walletResponse.data.message || 'Payment verification failed');
          }
        } catch (walletError: any) {
          // Both verifications failed
          setStatus('failed');
          const walletErrorMsg = walletError.response?.data?.detail || 'Payment verification failed';
          
          // If we have an order error message, include it for better debugging
          if (orderVerificationFailed && orderErrorMessage) {
            setMessage(`${walletErrorMsg}. (Order verification also failed: ${orderErrorMessage})`);
          } else {
            setMessage(walletErrorMsg);
          }
        }
      } catch (error: any) {
        setStatus('failed');
        setMessage(error.response?.data?.detail || 'Failed to verify payment');
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-blue-500 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment</h2>
              <p className="text-gray-600">Please wait while we confirm your payment...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              
              {paymentType === 'wallet' && amount && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Amount Added:</span>
                    <span className="text-xl font-bold text-green-600">
                      ₦{amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  {newBalance !== null && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">New Balance:</span>
                      <span className="text-xl font-bold text-blue-600">
                        ₦{newBalance.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {paymentType === 'order' && orderId && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Order ID:</span>
                    <span className="text-xl font-bold text-volexx-blue-600">
                      #{orderId}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Your order has been created and is being processed.
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-500">
                {paymentType === 'order' ? 'Redirecting to your orders...' : 'Redirecting to dashboard...'}
              </p>
            </div>
          )}

          {status === 'failed' && (
            <div className="text-center">
              <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <XCircleIcon className="h-12 w-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              
              <button
                onClick={() => navigate('/customer-dashboard', { replace: true })}
                className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
